import {
  ArrowRightIcon,
  BookOpenTextIcon,
  BrainIcon,
  GitBranchIcon,
  PackageIcon,
} from "@phosphor-icons/react/dist/ssr";
import { Link } from "zudoku/components";
import MCPIcon from "../icons/MCPServer.js";

const products = [
  {
    href: "/api-management/overview",
    icon: PackageIcon,
    title: "API Management",
    description:
      "Secure your APIs with authentication, rate limiting, and programmable policies.",
    color: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  },
  {
    href: "/ai-gateway/overview",
    icon: BrainIcon,
    title: "AI Gateway",
    description:
      "Connect AI providers through one API with usage tracking and cost controls.",
    color: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  },
  {
    href: "/mcp-server/overview",
    icon: MCPIcon,
    title: "MCP Server",
    description:
      "Turn your APIs into tools that AI assistants can discover and call.",
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  },
  {
    href: "/mcp-gateway/overview",
    icon: GitBranchIcon,
    title: "MCP Gateway",
    description:
      "Control access to remote MCP servers with authentication and tool curation.",
    color: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  },
  {
    href: "/dev-portal/overview",
    icon: BookOpenTextIcon,
    title: "Developer Portal",
    description:
      "Give developers a place to explore your API, test requests, and manage their API keys.",
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  },
];

const guideSteps = [
  [
    "Choose what to build",
    "An API, an AI gateway, an MCP server, or a developer portal.",
  ],
  [
    "Pick your workflow",
    "Use the Zuplo Portal, your editor, or an AI coding agent.",
  ],
  ["Make it work", "Follow the tutorial and test your first request."],
];

export function DocsHome() {
  return (
    <div className="not-prose max-w-6xl">
      <p className="mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
        Build and manage APIs for developers and AI. Start with a working
        project, or explore the docs for the product you use.
      </p>
      <section
        aria-labelledby="home-getting-started"
        className="relative isolate mb-12 overflow-hidden rounded-2xl border border-border bg-muted/20"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(255,0,189,0.07),transparent_65%)]"
        />
        <div className="grid gap-10 p-6 sm:p-8 lg:grid-cols-[1.1fr_1fr] lg:gap-14 lg:p-10">
          <div className="flex flex-col items-start">
            <span className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <span
                aria-hidden="true"
                className="size-1.5 rounded-full bg-primary"
              />
              Getting started
            </span>
            <h2
              id="home-getting-started"
              className="!m-0 max-w-md text-3xl font-semibold leading-tight tracking-tight text-foreground"
            >
              Build your first project with Zuplo
            </h2>
            <p className="mb-6 mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              Choose a guided tutorial for what you want to build. Each one
              takes you through setup, configuration, and a working example.
            </p>
            <Link
              to="/getting-started"
              className="group mt-auto inline-flex items-center gap-3 rounded-lg bg-foreground px-5 py-3 text-sm font-semibold !text-background no-underline transition-opacity hover:opacity-85 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
            >
              Start building
              <ArrowRightIcon
                aria-hidden="true"
                size={18}
                className="transition-transform motion-safe:group-hover:translate-x-1"
              />
            </Link>
          </div>
          <ol className="m-0 flex list-none flex-col justify-center gap-6 p-0 lg:border-l lg:border-border lg:pl-10">
            {guideSteps.map(([title, description], index) => (
              <li key={title} className="flex gap-4">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background text-xs font-medium tabular-nums text-muted-foreground"
                >
                  {index + 1}
                </span>
                <div>
                  <p className="m-0 text-sm font-semibold text-foreground">
                    {title}
                  </p>
                  <p className="mb-0 mt-1 text-sm leading-relaxed text-muted-foreground">
                    {description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section aria-labelledby="home-products">
        <h2
          id="home-products"
          className="!m-0 text-xl font-semibold tracking-tight text-foreground"
        >
          Explore the products
        </h2>
        <p className="mb-5 mt-2 text-sm text-muted-foreground">
          Concepts, guides, and reference for each part of Zuplo.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {products.map(({ icon: Icon, ...product }) => (
            <Link
              key={product.href}
              to={product.href}
              className="group flex items-start gap-4 rounded-xl border border-border bg-card p-5 no-underline transition-colors hover:border-foreground/25 hover:bg-muted/30 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring last:sm:col-span-2"
            >
              <span
                className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${product.color}`}
                aria-hidden="true"
              >
                <Icon size={24} />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="m-0 text-base font-semibold text-foreground">
                  {product.title}
                </h3>
                <p className="mb-0 mt-1.5 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  {product.description}
                </p>
              </div>
              <ArrowRightIcon
                aria-hidden="true"
                size={18}
                className="mt-1 shrink-0 text-muted-foreground transition-transform motion-safe:group-hover:translate-x-1"
              />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
